import React from 'react';
import { useForm } from 'react-hook-form';
import { faWarning } from '@fortawesome/free-solid-svg-icons/faWarning';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Button, {
  BUTTON_SIZES,
  BUTTON_TYPES,
  BUTTON_WIDTHS
} from 'buyside/components/presentation/Button/button';
import QuickPositionDescriptionModal from 'buyside/components/presentation/QuickPosition/OpenQuickPositionForm/quickPositionDescriptionModal';

import UpArrowIcon from 'buyside/svgs/up_arrow.svg';
import DownArrowIcon from 'buyside/svgs/down_arrow.svg';

import {
  CreateQuickPositionInput,
  Maybe,
  useCreateQuickPositionMutation
} from 'interfaces/graphql-typings';

import styles from './openQuickPositionFormStyles.scss';

const QuickPositionForm: React.FC<{
  assetId: string;
  currencyCode: Maybe<string>;
  isCurrentUserHidden: boolean;
  symbol: Maybe<string>;
  latestPrice: Maybe<number>;
}> = ({ assetId, currencyCode, isCurrentUserHidden, symbol, latestPrice }) => {
  const [
    createQuickPosition,
    { loading: createQuickPositionLoading, error }
  ] = useCreateQuickPositionMutation({
    update: (cache, { data }) => {
      const quickPosition = data?.createQuickPosition?.quickPosition;

      if (!quickPosition) return;

      const { id, asset, direction, openedAt, openingPrice } = quickPosition;
      const { longQuickPositionRatio, shortQuickPositionRatio } = asset;

      cache.modify({
        id: `Asset:${assetId}`,
        fields: {
          currentUserQuickPosition: () => ({
            id,
            direction,
            openedAt,
            openingPrice,
            returnToDate: 0
          }),
          longQuickPositionRatio: () => longQuickPositionRatio,
          shortQuickPositionRatio: () => shortQuickPositionRatio
        }
      });
    }
  });

  const { handleSubmit, register, reset, setValue, watch } = useForm<CreateQuickPositionInput>({
    defaultValues: {
      anonymous: isCurrentUserHidden,
      assetId,
      direction: ''
    }
  });

  const onSubmit = handleSubmit(variables =>
    createQuickPosition({ variables }).then(() => reset())
  );

  return (
    <form className={styles.quickPositionForm} onSubmit={onSubmit}>
      <div className={styles.quickPositionPrompt}>
        {latestPrice ? (
          <>
            Take a {<QuickPositionDescriptionModal />} on <strong>{symbol}</strong> at{' '}
            <strong>
              {latestPrice} {currencyCode}
            </strong>
          </>
        ) : (
          <>
            Latest price pending. Please check back later to take a{' '}
            {<QuickPositionDescriptionModal />} on <strong>{symbol}</strong>.
          </>
        )}
      </div>
      <div className={styles.directionButtons}>
        <Button
          buttonType={BUTTON_TYPES.BUTTON}
          disabled={createQuickPositionLoading || !latestPrice}
          icon={UpArrowIcon}
          onClick={(): void => setValue('direction', 'long', { shouldDirty: true })}
          size={BUTTON_SIZES.TINY}
          text="Long"
          type={watch('direction') === 'long' ? BUTTON_TYPES.MAJOR : BUTTON_TYPES.SECONDARY}
          width={BUTTON_WIDTHS.FULL}
        />
        <Button
          buttonType={BUTTON_TYPES.BUTTON}
          disabled={createQuickPositionLoading || !latestPrice}
          icon={DownArrowIcon}
          onClick={(): void => setValue('direction', 'short', { shouldDirty: true })}
          size={BUTTON_SIZES.TINY}
          text="Short"
          type={watch('direction') === 'short' ? BUTTON_TYPES.DANGER : BUTTON_TYPES.SECONDARY}
          width={BUTTON_WIDTHS.FULL}
        />
      </div>
      {!isCurrentUserHidden && (
        <label className={styles.anonymousCheckbox}>
          <input
            disabled={createQuickPositionLoading || !latestPrice}
            type="checkbox"
            {...register('anonymous')}
          ></input>
          Do not disclose my name. I prefer not to be contacted by other members to discuss my
          position on this security.
        </label>
      )}
      {error && (
        <label className={styles.errorMessage}>
          <FontAwesomeIcon icon={faWarning} />
          {error.message}
        </label>
      )}
      <Button
        disabled={watch('direction') === ''}
        isLoading={createQuickPositionLoading}
        size={BUTTON_SIZES.TINY}
        text="Submit"
        type={BUTTON_TYPES.PRIMARY}
        width={BUTTON_WIDTHS.FULL}
      />
    </form>
  );
};

export default QuickPositionForm;
